// dependencies
import React, { useState, useEffect, useMemo } from 'react'
import { arrayOf, number, object, shape, string, bool } from 'prop-types'
import { useTheme, styled, useMediaQuery, Button, Grid, Typography, Box } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import _isEmpty from 'lodash/isEmpty'
import _isEqual from 'lodash/isEqual'
// helpers
import { abbreviateState } from '@helpers/geo-location'
import { decodeTrademarkEntity } from '@helpers/string-helper'
import { loadMore } from '@helpers/array-helper'
import { getMarkers, groupStoresByAddress } from '@helpers/store-locator'
// components
import { useMyStore, FETCH_PROPERTIES as MYSTORE_FETCH_PROPERTIES } from '@hooks/useMyStore'
import useStoreLocatorContext from '@context/storeLocatorContext/useStoreLocatorContext'
import RadarMap from '@components/store-locator/RadarMap/RadarMap'
import { useProduct, FETCH_PROPERTIES as PRODUCT_FETCH_PROPERTIES } from '../product/hooks'
import StoreSearchBar from './search-bar'
import MapLocationsList from './map-locations-list'
import '../../assets/css/components/store-locator/store-locator.sass'

const StyledLoadMoreButton = styled(Button)(({ theme }) => ({
  marginTop: '1rem !important',
  alignSelf: 'center',
  margin: '1em',
  width: 'calc(100% - 2em)',
}))

const StyledGridWrapper = styled(Grid)(({ theme }) => ({
  height: '100%',
  overflowY: 'scroll',
  [theme.breakpoints.down('md')]: {
    height: '100dvh',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}))

const StyledGridListContainer = styled(Grid)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '#store-search-bar': {
    backgroundColor: '#f6f8fb',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    overflowY: 'scroll',
    flex: 1,
  },
}))

const StyledProductContainer = styled(Box)(({ theme, bgImage }) => ({
  display: 'flex',
  flex: '1 0 auto',
  justifyContent: 'center',
  position: 'relative',
  backgroundColor: `#f6f6f6`,
  backgroundImage: `url(${bgImage})`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  textAlign: 'center',
  height: '230px',

  transform: 'translateZ(-0.3px) scale(1.3)',
  zIndex: '-1',

  [theme.breakpoints.down('md')]: {
    zIndex: 1,
    flexDirection: 'row',
    transform: 'none',
    top: 'unset',
    width: '100px',
    height: '67px',
    // float: 'left',
    marginTop: '10px',
    marginRight: '15px',
    '.MuiBox-root': {
      width: '65%',
      paddingBottom: 0,
      picture: {
        img: {
          height: '66px',
          width: '100px',
          objectFit: 'cover',
        },
      },
    },
  },
}))

const StyledMobileProductContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginRight: '-15px',
  marginLeft: '-15px',
  backgroundColor: `${theme.palette.common.white} !important`,
  '*': {
    backgroundColor: `${theme.palette.common.white} !important`,
    marginTop: '0',
    padding: '15px',
  },
  img: {
    zIndex: 1,
    maxWidth: '100px',
    maxHeight: '85px',
    padding: '10px!important',
  },
}))

const StyledGridMapContainer = styled(Grid)(({ theme }) => ({
  height: '100%',
  '& > div': {
    height: '100%',
  },
  [theme.breakpoints.down('md')]: {
    flex: 1,
    maxHeight: 'min(50vh, 400px)',
  },
}))

const StyledListContainer = styled(Grid)(({ theme }) => ({
  perspective: '1px',
  perspectiveOrigin: 'center 130px',
  transformStyle: 'preserve-3d',
  height: '100vh',
  overflowX: 'hidden',
  overflowY: 'scroll',
  // height: '100%',
  padding: '15px',
  backgroundColor: theme.palette.common.white,

  [theme.breakpoints.down('md')]: {
    perspectiveOrigin: 'center 115px',
  },
}))

const StyledUnavailableInfoContainer = styled(Typography)(({ theme }) => ({
  marginBottom: '16px',
  display: 'flex',
  flexDirection: 'row',
  position: 'relative',
  backgroundColor: '#f6f8fb',
  p: {
    color: theme.palette.primary.dark,
    fontWeight: 400,
    fontSize: '16px',
    marginLeft: '12px',
  },
}))

const StyledNameText = styled(Typography)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.common.white,
  padding: '10px 15px',
  marginRight: '-15px',
  marginLeft: '-15px',
  [theme.breakpoints.down('md')]: {
    width: '100vw',
  },
}))

const StyledSeeInStoreText = styled(Typography)(({ theme }) => ({
  color: theme.palette.success.main,
  backgroundColor: theme.palette.common.white,
  position: 'relative',
  fontWeight: 500,
  fontSize: '19px',
  padding: '0 15px',
  marginRight: '-15px',
  marginLeft: '-15px',
  [theme.breakpoints.down('md')]: {
    width: '100vw',
  },
}))

const StyledStoreSearchBar = styled(StoreSearchBar)(({ theme, isProductInStore }) => ({
  position: 'relative',
  marginLeft: '-15px',
  marginRight: '-15px',
  backgroundColor: isProductInStore ? '#f6f8fb' : theme.palette.common.white,
  ...(!isProductInStore && { borderBottom: `1px solid ${theme.palette.divider}`, paddingBottom: '20px' }),
  '&>*': {
    margin: '0 16px!important',
  },
  '.MuiInputBase-root': {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
  },
}))

const StoreMap = ({ storeLocations, myStore, isMyStore, productInStore }) => {
  const map = useStoreLocatorContext(state => state.mapInstance)
  const myStoreInContext = useStoreLocatorContext(state => state.myStore)
  const setMyStoreInContext = useStoreLocatorContext(state => state.setMyStore)
  const setStores = useStoreLocatorContext(state => state.setStores)
  const stores = useStoreLocatorContext(state => state.stores)
  const currentPosition = useStoreLocatorContext(state => state.currentPosition)
  const [pageNumber, setPageNumber] = useState(1)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { availability, city, state } = useProduct({ product: productInStore }, [
    PRODUCT_FETCH_PROPERTIES.AVAILABILITY_STOCKMESSAGE,
  ])

  // myStore updates frequently in Gatsby's state, which results in excessive rendering and buggy behavior
  // in the Radar Map.  This use effect utilizes "myStoreInContext", which only triggers recalculations
  // when the myStore values actually change.
  useEffect(() => {
    if (!_isEqual(myStoreInContext, myStore) && !!myStore?.storeName) {
      setMyStoreInContext(myStore)
    }
  }, [myStore, myStoreInContext, setMyStoreInContext, map])

  const { allStrapiStores, groupedStrapiStores, strapiStoresLoadedFlag } = useMyStore(
    {},
    MYSTORE_FETCH_PROPERTIES.ALL_STORES,
  )
  const allStoreLocations = useMemo(() => {
    const allStores = !_isEmpty(allStrapiStores) ? allStrapiStores : storeLocations
    return allStores?.map(strapiStore => ({
      ...strapiStore,
      types: groupedStrapiStores?.find(groupedStore => strapiStore?.storeNumber === groupedStore?.storeNumber)?.types,
    }))
  }, [allStrapiStores, groupedStrapiStores, storeLocations])

  const seeInStoresUnavailableProduct =
    myStoreInContext?.storeNumber && productInStore && !productInStore.isProductInStore

  const renderSearchBar = () => (
    <>
      {seeInStoresUnavailableProduct && (
        <StyledUnavailableInfoContainer>
          <InfoOutlined />
          <Typography>
            {availability && !paginatedStores?.length
              ? `Unavailable to see at stores within 100 miles, but available for delivery to ${city}, ${abbreviateState(
                  state,
                )}  `
              : `Unavailable at ${myStore?.storeExtendedLocationAndName}`}
          </Typography>
        </StyledUnavailableInfoContainer>
      )}
      <StyledStoreSearchBar id="store-search-bar" title={storeSearchBarTitle} isProductInStore={productInStore} />
    </>
  )

  const markers = useMemo(
    () => (currentPosition?.lat ? getMarkers(currentPosition.lat, currentPosition.lng, allStoreLocations) : []),
    [currentPosition.lat, currentPosition.lng, allStoreLocations],
  )

  const groupedStores = useMemo(() => {
    const tempGroupedStores = groupStoresByAddress(markers)
    return tempGroupedStores?.filter(groupedStore => groupedStore?.distance <= 100)
  }, [markers])

  const paginatedStores = useMemo(() => {
    if (!isMobile) return groupedStores
    return loadMore(groupedStores, pageNumber)
  }, [isMobile, groupedStores, pageNumber])

  const filteredPaginatedStores = useMemo(
    () => {
      // New condition, ONLY -> OUTLET and EXPRESS should be removed from PaginatedStores - https://rtgjira.atlassian.net/browse/FD-4120
      if (!isMyStore) {
        return paginatedStores
      }
      return paginatedStores?.filter(
        paginatedStore =>
          !paginatedStore?.name?.toLowerCase()?.includes('outlet') &&
          !paginatedStore?.name?.toLowerCase()?.includes('express'),
      )
    },
    // paginatedStores,
    [isMyStore, paginatedStores],
  )

  useEffect(() => {
    if (strapiStoresLoadedFlag) setStores(filteredPaginatedStores)
  }, [filteredPaginatedStores, setStores, strapiStoresLoadedFlag])

  const storeSearchBarTitle = useMemo(() => {
    if (
      (seeInStoresUnavailableProduct && availability && !stores?.length) ||
      (productInStore?.isProductInStore && stores?.length === 1)
    ) {
      return 'Search within new location:'
    }

    return (
      productInStore &&
      `${
        productInStore?.isProductInStore ? 'Also, ' : ''
      } available to see at the following stores or search for new location:`
    )
  }, [availability, stores, productInStore, seeInStoresUnavailableProduct])

  return (
    <StyledGridWrapper container>
      <StyledGridListContainer item xs={12} md={5.2} order={{ xs: 2, md: 1 }}>
        <StyledListContainer>
          {productInStore && (
            <>
              {isMobile ? (
                <StyledMobileProductContainer>
                  <img src={`${productInStore?.primary_image}&w=250`} alt={productInStore?.title} />
                  <StyledNameText>{decodeTrademarkEntity(productInStore?.title)}</StyledNameText>
                </StyledMobileProductContainer>
              ) : (
                <>
                  <StyledProductContainer bgImage={`${productInStore?.primary_image}&w=460`} />
                  <StyledNameText>{decodeTrademarkEntity(productInStore?.title)}</StyledNameText>
                </>
              )}
              {productInStore?.isProductInStore && <StyledSeeInStoreText>See in Store</StyledSeeInStoreText>}
            </>
          )}
          {!productInStore && renderSearchBar()}
          <MapLocationsList
            defaultStores={storeLocations}
            isMyStore={isMyStore}
            productInStore={{ ...productInStore, availability }}
            renderSearchBar={renderSearchBar}
            showRatings
          />
          {isMobile && !!filteredPaginatedStores.length && filteredPaginatedStores.length < markers.length && (
            <StyledLoadMoreButton
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              sx={{ mt: 1 }}
              value="View more"
              gtm-category="view more stores"
              gtm-action="view more stores"
              onClick={() => setPageNumber(prevPageNumber => prevPageNumber + 1)}
            >
              VIEW MORE
            </StyledLoadMoreButton>
          )}
        </StyledListContainer>
      </StyledGridListContainer>
      <StyledGridMapContainer item xs={12} md={6.8} order={{ xs: 1, md: 2 }}>
        <RadarMap variant="store-locator-modal" includeCurrentMarker />
      </StyledGridMapContainer>
    </StyledGridWrapper>
  )
}

StoreMap.propTypes = {
  storeLocations: arrayOf(
    shape({
      Address1: string,
      City: string,
      Location: shape({
        latLng: shape({
          lat: number,
          lng: number,
        }),
      }),
      State: string,
      StoreHours: object,
      StoreNumber: number,
      StoreType: string,
      Zip: string,
    }),
  ),
  myStore: object,
  isMyStore: bool,
  productInStore: string,
}

export default StoreMap
